import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, Award, FlaskConical, CheckCircle, Leaf, Package, ThermometerSun, Eye } from 'lucide-react';

const HING_IMAGE = "https://static.prod-images.emergentagent.com/jobs/ff8af06a-976d-4efd-824f-28da774a6e41/images/cd9f10a94af263e218aa96fcc41f3053bda0e23a8389e81b98cc024b18cc0495.png";

const QualityPage = () => {
  const qualitySteps = [
    {
      icon: <Leaf className="w-8 h-8" />,
      title: "Raw Material Sourcing",
      description: "We source the finest quality asafoetida resin from trusted suppliers who follow sustainable harvesting practices."
    },
    {
      icon: <FlaskConical className="w-8 h-8" />,
      title: "Laboratory Testing",
      description: "Every batch undergoes comprehensive lab testing for purity, aroma strength, and absence of adulterants."
    },
    {
      icon: <ThermometerSun className="w-8 h-8" />,
      title: "Controlled Processing",
      description: "State-of-the-art processing in temperature-controlled environments to preserve natural properties."
    },
    {
      icon: <Package className="w-8 h-8" />,
      title: "Hygienic Packaging",
      description: "Air-tight, food-grade packaging ensures freshness and prevents moisture absorption."
    },
    {
      icon: <Eye className="w-8 h-8" />,
      title: "Quality Inspection",
      description: "Final quality check before dispatch to ensure every packet meets our premium standards."
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: "Traceability",
      description: "Complete batch traceability from source to shelf for transparency and accountability."
    }
  ];

  const certifications = [
    {
      title: "GST Registered",
      description: "Fully compliant with Goods & Services Tax regulations",
      number: "09ABSCS7424E1ZW"
    },
    {
      title: "FSSAI Standards",
      description: "Adherence to Food Safety and Standards Authority guidelines"
    },
    {
      title: "Quality Certified",
      description: "Meeting national quality standards for spice products"
    }
  ];

  const qualityPromises = [
    "100% Pure Asafoetida - No Adulterants",
    "No Artificial Colors or Flavors",
    "No Chemical Preservatives",
    "Consistent Quality in Every Batch",
    "Full Transparency in Ingredients",
    "Ethical Sourcing Practices"
  ];

  return (
    <div data-testid="quality-page">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-orange-600 to-orange-800 text-white py-24" data-testid="quality-hero">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="quality-badge mb-6">Quality Assured</div>
              <h1 className="font-display text-4xl md:text-5xl font-bold mb-6">
                Our Commitment to Quality
              </h1>
              <p className="text-xl text-white/90 leading-relaxed">
                At Punjabi Chaska, quality is not just a standard—it's our promise. 
                Every step of our production process is designed to deliver 
                the purest, most authentic Hing to your kitchen.
              </p>
            </div>
            <div className="flex justify-center">
              <div className="relative">
                <img 
                  src={HING_IMAGE} 
                  alt="Quality Spices" 
                  className="rounded-xl shadow-2xl max-w-md w-full"
                />
                <div className="absolute -bottom-6 -right-6 bg-white text-gray-900 p-6 rounded-xl shadow-lg">
                  <Award className="w-10 h-10 text-brand-orange mb-2" />
                  <span className="font-semibold">Premium Grade</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quality Process */}
      <section className="py-20 bg-white" data-testid="quality-process">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-16">
            <h2 className="section-title">Our Quality Process</h2>
            <p className="section-subtitle">
              From sourcing to packaging, every step is meticulously controlled
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {qualitySteps.map((step, index) => (
              <div 
                key={index}
                className="bg-white border border-gray-100 p-8 rounded-xl hover:shadow-lg transition-all duration-300"
                data-testid={`quality-step-${index}`}
              >
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-14 h-14 rounded-full bg-orange-100 flex items-center justify-center text-brand-orange">
                    {step.icon}
                  </div>
                  <div>
                    <span className="text-sm text-brand-orange font-semibold">Step {index + 1}</span>
                    <h3 className="text-xl font-semibold mb-2">{step.title}</h3>
                    <p className="text-gray-600 text-sm">{step.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Quality Promise */}
      <section className="py-20 bg-brand-cream" data-testid="quality-promise">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="section-title mb-8">Our Quality Promise</h2>
              <div className="space-y-4">
                {qualityPromises.map((promise, index) => (
                  <div key={index} className="flex items-center gap-4 bg-white p-4 rounded-lg shadow-sm">
                    <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0" />
                    <span className="text-gray-800">{promise}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-white p-10 rounded-2xl shadow-lg">
              <h3 className="text-2xl font-semibold mb-6 text-center">Why Purity Matters</h3>
              <p className="text-gray-600 leading-relaxed mb-6">
                Hing (Asafoetida) is a delicate spice that can easily be adulterated. 
                Poor quality Hing not only lacks the authentic aroma and flavor but 
                can also contain harmful substances. That's why we go the extra mile 
                to ensure our Hing is:
              </p>
              <ul className="space-y-3 text-gray-600">
                <li className="flex items-start gap-3">
                  <span className="text-brand-orange font-bold">•</span>
                  <span>Sourced from verified, reputable suppliers</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-brand-orange font-bold">•</span>
                  <span>Tested for purity and authenticity</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-brand-orange font-bold">•</span>
                  <span>Free from fillers and adulterants</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-brand-orange font-bold">•</span>
                  <span>Processed in hygienic conditions</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Certifications */}
      <section className="py-20 bg-gray-900 text-white" data-testid="certifications">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-16">
            <h2 className="font-display text-4xl font-bold mb-4">Certifications & Compliance</h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              We maintain all necessary certifications and comply with regulatory standards
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {certifications.map((cert, index) => (
              <div 
                key={index}
                className="bg-gray-800 p-8 rounded-xl text-center"
                data-testid={`certification-${index}`}
              >
                <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-orange-500/20 flex items-center justify-center">
                  <Shield className="w-8 h-8 text-orange-400" />
                </div>
                <h3 className="text-xl font-semibold mb-3">{cert.title}</h3>
                <p className="text-gray-400 text-sm mb-4">{cert.description}</p>
                {cert.number && (
                  <span className="font-mono text-orange-400 text-sm">{cert.number}</span>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-brand-orange" data-testid="quality-cta">
        <div className="max-w-4xl mx-auto px-4 md:px-8 text-center text-white">
          <h2 className="font-display text-3xl md:text-4xl font-bold mb-6">
            Experience the Difference Quality Makes
          </h2>
          <p className="text-xl text-white/90 mb-8">
            Try our premium Hing and taste the purity in every dish
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/products" className="btn-outline" data-testid="view-products-btn">
              View Products
            </Link>
            <Link to="/contact" className="btn-outline" data-testid="contact-btn">
              Contact Us
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default QualityPage;
